import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  formatMoney,
  fromCents,
  type InvoiceBankCandidate,
  type InvoiceBankCandidatesResponse,
  invoiceBankError,
  type InvoiceBankLinkRequest,
  type InvoiceResponse,
  parseDecimal,
  toCents,
} from '@k-management/shared';
import { useState } from 'react';
import { DecimalInput } from '../../components/DecimalInput.js';
import { Field } from '../../components/Field.js';
import { amountSort, SortHeader, useTableSort } from '../../components/SortHeader.js';
import { apiRequest } from '../../lib/api.js';
import { formatDate } from '../../lib/format.js';
import { treasuryKeys } from './treasury-ui.js';

interface Period {
  from?: string;
  to?: string;
  all: boolean;
}

/**
 * «Cerca in banca» (1.72): the movements of the banks, and the spending with
 * the cards for a received invoice, that may collect or pay the invoice,
 * ranked as the reconciliation from the movement ranks them; one or more are
 * chosen with their part and confirmed together.
 */
export function InvoiceBankPanel({
  invoice,
  onClose,
  onSaved,
}: {
  invoice: InvoiceResponse;
  onClose: () => void;
  onSaved: (updated: InvoiceResponse) => Promise<void>;
}) {
  const queryClient = useQueryClient();
  const issued = invoice.direction === 'emessa';
  const [period, setPeriod] = useState<Period>({ all: false });
  const [account, setAccount] = useState('');
  const [search, setSearch] = useState('');
  const [proposedOnly, setProposedOnly] = useState(true);
  // The amount chosen for each movement ticked; null until the first answer ticks the best.
  const [chosen, setChosen] = useState<Record<string, string> | null>(null);
  const query = new URLSearchParams();
  if (period.all) query.set('all', 'true');
  if (period.from) query.set('from', period.from);
  if (period.to) query.set('to', period.to);
  const data = useQuery({
    queryKey: [...treasuryKeys.invoice(invoice.id), 'bank', query.toString()],
    queryFn: () =>
      apiRequest<InvoiceBankCandidatesResponse>(
        `/treasury/invoices/${invoice.id}/bank-candidates?${query.toString()}`,
      ),
  });
  const found = data.data;
  const candidates = found?.candidates ?? [];
  const ticked =
    chosen ??
    Object.fromEntries(
      candidates.filter((item) => item.best).map((item) => [item.id, item.proposed]),
    );
  const save = useMutation({
    mutationFn: (links: InvoiceBankLinkRequest['links']) =>
      apiRequest<InvoiceResponse>(`/treasury/invoices/${invoice.id}/bank-links`, {
        method: 'POST',
        body: { links } satisfies InvoiceBankLinkRequest,
      }),
    onSuccess: async (updated) => {
      // Closed first: the invoice updated would make what was chosen look too much.
      onClose();
      await onSaved(updated);
      // The movements and the cards changed too.
      await queryClient.invalidateQueries({ queryKey: treasuryKeys.all });
    },
  });

  const proposals = candidates.filter((item) => item.score > 0);
  const words = search.trim().toLowerCase();
  const shown = (proposedOnly && proposals.length ? proposals : candidates).filter(
    (item) =>
      item.id in ticked ||
      ((!account || item.label === account) &&
        (!words ||
          `${item.description} ${item.counterparty ?? ''} ${formatMoney(item.amount)}`
            .toLowerCase()
            .includes(words))),
  );
  const sorting = useTableSort('invoice-bank', shown, {
    date: (item) => item.date,
    label: (item) => item.label,
    description: (item) => item.description,
    amount: (item) => amountSort(item.amount),
    free: (item) => amountSort(item.free),
  });
  const selected = candidates
    .filter((item) => item.id in ticked)
    .map((item) => ({ item, number: parseDecimal(ticked[item.id]!) }));
  const links = selected.map(({ item, number }) => ({
    source: item.source,
    movementId: item.id,
    amount: number === null ? '0.00' : number.toFixed(2),
  }));
  const parts = selected.map(({ item }, index) => ({
    label: `${item.label} del ${formatDate(item.date)}`,
    free: item.free,
    amount: links[index]!.amount,
    // Equal to the payment marked by hand: it becomes it, adding nothing.
    manual: item.manual !== null && links[index]!.amount === item.manual.amount,
  }));
  const error = parts.length ? invoiceBankError(invoice.residual, parts) : null;
  const added = parts
    .filter((part) => !part.manual)
    .reduce((sum, part) => sum + toCents(part.amount), 0);
  const left = Math.max(toCents(invoice.residual), 0) - added;
  const toggle = (item: InvoiceBankCandidate) =>
    setChosen(() => {
      const current = { ...ticked };
      if (item.id in current) {
        delete current[item.id];
        return current;
      }
      // As much as the movement has free and the invoice still needs.
      const cents = item.manual
        ? toCents(item.proposed)
        : Math.min(toCents(item.free), Math.max(left, 0));
      return { ...current, [item.id]: fromCents(cents) };
    });
  const accounts = [...new Set(candidates.map((item) => item.label))].sort((a, b) =>
    a.localeCompare(b, 'it'),
  );

  return (
    <div className="stack">
      <div className="row between">
        <h3>{issued ? 'Movimenti in entrata' : 'Movimenti in uscita e spese con carta'}</h3>
        <button type="button" className="link" onClick={onClose}>
          Chiudi
        </button>
      </div>
      <div className="toolbar-inline">
        <Field label="Conto">
          <select value={account} onChange={(event) => setAccount(event.target.value)}>
            <option value="">Tutti i conti</option>
            {accounts.map((label) => (
              <option key={label} value={label}>
                {label}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Dal">
          <input
            type="date"
            value={period.all ? '' : (period.from ?? found?.from ?? '')}
            onChange={(event) =>
              setPeriod({ ...period, all: false, from: event.target.value || undefined })
            }
          />
        </Field>
        <Field label="Al">
          <input
            type="date"
            value={period.all ? '' : (period.to ?? found?.to ?? '')}
            onChange={(event) =>
              setPeriod({ ...period, all: false, to: event.target.value || undefined })
            }
          />
        </Field>
        <button
          type="button"
          className={period.all ? 'filter-changed' : undefined}
          onClick={() => setPeriod(period.all ? { all: false } : { all: true })}
        >
          {period.all ? 'Periodo proposto' : 'Tutto il periodo'}
        </button>
        <Field label="Cerca">
          <input
            type="search"
            placeholder="Descrizione o importo"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </Field>
        <label className="checkbox">
          <input
            type="checkbox"
            checked={proposedOnly}
            onChange={(event) => setProposedOnly(event.target.checked)}
          />{' '}
          solo i movimenti proposti
        </label>
      </div>
      {found && found.coverage.length > 0 && (
        <p className="muted small">
          Estratti caricati:{' '}
          {found.coverage.map((item, index) => (
            <span key={item.label}>
              {index > 0 && ' · '}
              <strong>
                {item.label} fino al {item.to ? formatDate(item.to) : '—'}
              </strong>
            </span>
          ))}
          . {issued ? 'Un incasso' : 'Un pagamento'} più recente non c’è ancora.
        </p>
      )}
      {data.isPending && <p className="muted">Caricamento…</p>}
      {data.isError && <p className="error">{data.error.message}</p>}
      {found && candidates.length === 0 && (
        <p className="muted">
          Nessun movimento libero nel periodo: allarga il periodo o controlla che l’estratto sia
          caricato.
        </p>
      )}
      {found && candidates.length > 0 && proposedOnly && proposals.length === 0 && (
        <p className="notice small">
          Nessun movimento somiglia alla fattura: sotto ci sono tutti quelli del periodo.
        </p>
      )}
      {shown.length > 0 && (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <SortHeader sort={sorting} column="date">
                  Data
                </SortHeader>
                <SortHeader sort={sorting} column="label">
                  Conto
                </SortHeader>
                <SortHeader sort={sorting} column="description">
                  Descrizione della banca
                </SortHeader>
                <SortHeader sort={sorting} column="amount" numeric>
                  Importo
                </SortHeader>
                <SortHeader sort={sorting} column="free" numeric>
                  Non ancora assegnato
                </SortHeader>
                <th className="numeric">Alla fattura</th>
              </tr>
            </thead>
            <tbody>
              {sorting.rows.map((item) => {
                const checked = item.id in ticked;
                return (
                  <tr key={item.id} className={item.best ? 'best' : undefined}>
                    <td>
                      <label className="checkbox">
                        <input type="checkbox" checked={checked} onChange={() => toggle(item)} />{' '}
                        {formatDate(item.date)}
                      </label>
                    </td>
                    <td>{item.label}</td>
                    <td className="details">
                      {item.description}
                      <span className="block">
                        {item.best && <span className="pill ok">consigliato</span>}
                        {item.reasons.map((reason) => (
                          <span key={reason} className="pill info">
                            {reason}
                          </span>
                        ))}
                        {item.manual && (
                          <span className="pill warn">
                            diventa {issued ? 'l’incasso segnato' : 'il pagamento segnato'} a mano
                            del {formatDate(item.manual.date)}
                          </span>
                        )}
                        {item.assigned.length > 0 && (
                          <span className="pill">già sulle fatture {item.assigned.join(', ')}</span>
                        )}
                        {item.item && (
                          <span className="pill">rendicontata sul conto {item.item}</span>
                        )}
                      </span>
                    </td>
                    <td className="numeric">{formatMoney(item.amount)}</td>
                    <td className="numeric">{formatMoney(item.free)}</td>
                    <td className="numeric">
                      {checked ? (
                        <DecimalInput
                          aria-label={`Alla fattura dal movimento del ${formatDate(item.date)}`}
                          value={ticked[item.id]!}
                          onChange={(value) => setChosen({ ...ticked, [item.id]: value })}
                          decimals={[2, 2]}
                        />
                      ) : (
                        '—'
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <div className="row between">
        <dl className="totals">
          <div>
            <dt>{issued ? 'Da incassare' : 'Da pagare'}</dt>
            <dd>{formatMoney(invoice.residual)}</dd>
          </div>
          <div>
            <dt>Dai movimenti scelti</dt>
            <dd>{formatMoney(fromCents(added))}</dd>
          </div>
          <div className="grand">
            <dt>Resta sulla fattura</dt>
            <dd>{formatMoney(fromCents(Math.max(left, 0)))}</dd>
          </div>
        </dl>
        <button
          type="button"
          className="primary"
          disabled={!links.length || Boolean(error) || save.isPending}
          onClick={() => save.mutate(links)}
        >
          {save.isPending ? 'Attendere…' : 'Conferma la riconciliazione'}
        </button>
      </div>
      {error && <p className="error">{error}</p>}
      {save.error && <p className="error">{save.error.message}</p>}
      <p className="muted small">
        È la stessa riconciliazione che si fa dal movimento: si vede anche in Banche (o nelle carte)
        e si annulla da lì. Un movimento che paga più fatture tiene le altre.
      </p>
    </div>
  );
}
